import { formatNumber } from '@nova/shared';
import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, YAxis } from 'recharts';
import { useChartTheme } from '../app/ThemeProvider.js';
import { areaColorFor } from '../lib/area-color.js';
import { cn } from '../lib/cn.js';

export interface StatCardProps {
  label: string;
  value: number | string;
  unit?: string;
  /** Previous value, used for the change arrow. */
  previous?: number | null;
  /** Dashed reference line in the sparkline. */
  target?: number | null;
  series?: { value: number }[];
  /** Set for KPIs where a smaller number is better (e.g. response time). */
  lowerIsBetter?: boolean;
  /** Area colour; falls back to the theme accent. */
  accentColor?: string;
  onClick?: () => void;
}

/**
 * KPI card: large tabular number, unit, change arrow and a sparkline with the
 * target as a dashed reference line (06-designsystem.md).
 */
export function StatCard({
  label,
  value,
  unit,
  previous,
  target,
  series,
  lowerIsBetter = false,
  accentColor,
  onClick,
}: StatCardProps) {
  const chart = useChartTheme();
  const stroke = accentColor ? areaColorFor(accentColor, chart.theme) : chart.accent;
  const numeric = typeof value === 'number' ? value : null;
  const change =
    numeric !== null && previous !== null && previous !== undefined && previous !== 0
      ? ((numeric - previous) / Math.abs(previous)) * 100
      : null;

  const improving = change === null ? null : lowerIsBetter ? change < 0 : change > 0;
  // The arrow follows the number; the colour says whether that is good news.
  const Arrow =
    change === null || Math.abs(change) < 0.05
      ? ArrowRight
      : change > 0
        ? ArrowUpRight
        : ArrowDownRight;

  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Wrapper
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'glass relative flex min-w-0 flex-col justify-between p-4 text-left',
        onClick && 'hover:border-accent/30 transition',
      )}
    >
      <p className="label-micro truncate" title={label}>
        {label}
      </p>

      <div className="mt-2 flex items-end gap-2">
        <span className="num-tabular font-display text-ink text-[32px] font-bold leading-none">
          {typeof value === 'number' ? formatNumber(value, value % 1 === 0 ? 0 : 1) : value}
        </span>
        {unit && <span className="text-ink-soft pb-1 text-xs">{unit}</span>}
      </div>

      <div className="mt-1 flex items-center gap-1.5">
        {change !== null && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-xs',
              improving === null || Math.abs(change) < 0.05
                ? 'text-ink-soft'
                : improving
                  ? 'text-success'
                  : 'text-danger',
            )}
          >
            <Arrow size={13} aria-hidden />
            {formatNumber(Math.abs(change), 1)} %
          </span>
        )}
        {target !== null && target !== undefined && (
          <span className="text-ink-soft text-xs">
            {change !== null && '· '}mål {formatNumber(target, target % 1 === 0 ? 0 : 1)}
          </span>
        )}
      </div>

      {series && series.length > 1 && (
        <div className="mt-3 h-10 w-full" aria-hidden>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient
                  id={`spark-${label.replace(/\W/g, '')}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              {/* Zoom the axis to the data (plus the target) — a 0-based axis
                  flattens series like "3 200 → 3 745 followers" into a line. */}
              <YAxis hide domain={sparklineDomain(series, target)} />
              {target !== null && target !== undefined && (
                <ReferenceLine
                  y={target}
                  stroke={chart.reference}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
              )}
              <Area
                type="monotone"
                dataKey="value"
                stroke={stroke}
                strokeWidth={2}
                fill={`url(#spark-${label.replace(/\W/g, '')})`}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Wrapper>
  );
}

/** Data range with 12 % headroom, widened to include the target line. */
function sparklineDomain(series: { value: number }[], target?: number | null): [number, number] {
  const values = series.map((d) => d.value);
  if (target !== null && target !== undefined) values.push(target);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = (max - min || Math.abs(max) || 1) * 0.12;
  return [min - padding, max + padding];
}

/** Compact counter used in the status row on mobile and above the plan. */
export function CountTile({
  label,
  value,
  tone = 'neutral',
  onClick,
  active = false,
}: {
  label: string;
  value: number;
  tone?: 'neutral' | 'accent' | 'warning' | 'danger' | 'success' | 'violet';
  onClick?: () => void;
  active?: boolean;
}) {
  const tones: Record<string, string> = {
    neutral: 'text-ink',
    accent: 'text-accent',
    warning: 'text-warning',
    danger: 'text-danger',
    success: 'text-success',
    violet: 'text-violet',
  };

  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      data-testid="count-tile"
      data-label={label}
      className={cn(
        'glass flex min-w-[92px] flex-col items-start gap-0.5 px-3 py-2.5 text-left transition',
        onClick && 'hover:border-accent/30',
        active && 'border-accent/50 bg-accent/[0.07]',
      )}
    >
      <span className={cn('num-tabular font-display text-xl font-bold leading-none', tones[tone])}>
        {formatNumber(value)}
      </span>
      <span className="label-micro truncate">{label}</span>
    </Wrapper>
  );
}
